import { Box, Typography, useTheme } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import React, { FC, ReactNode } from 'react';

export const reportKpiCardShell = (theme: Theme) => ({
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 0.75,
  py: 1.35,
  px: 1.5,
  borderRadius: 2,
  backgroundColor: theme.palette.common.white,
  border: '1px solid',
  borderColor: 'divider',
  minHeight: 100,
  height: '100%',
  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
  '&:hover': {
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
});

export const ReportKpiHeader: FC<{
  icon: ReactNode;
  title: string;
  iconColor?: string;
  iconBg?: string;
}> = ({ icon, title, iconColor, iconBg }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: '50%',
        bgcolor: iconBg || 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: iconColor || 'primary.main',
      }}>
      {icon}
    </Box>
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        color: 'text.secondary',
        textTransform: 'uppercase',
        letterSpacing: 0.35,
        lineHeight: 1.2,
      }}>
      {title}
    </Typography>
  </Box>
);

interface ReportKpiCardProps {
  icon: ReactNode;
  title: string;
  value: ReactNode;
  subtitle?: ReactNode;
  iconColor?: string;
  iconBg?: string;
  valueColor?: string;
  onClick?: () => void;
  selected?: boolean;
}

const ReportKpiCard: FC<ReportKpiCardProps> = ({
  icon,
  title,
  value,
  subtitle,
  iconColor,
  iconBg,
  valueColor,
  onClick,
  selected,
}) => {
  const theme = useTheme();

  return (
    <Box
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={{
        ...reportKpiCardShell(theme),
        cursor: onClick ? 'pointer' : 'default',
        borderColor: selected ? 'primary.main' : 'divider',
        boxShadow: selected ? '0 2px 8px rgba(0,0,0,0.08)' : undefined,
        outline: 'none',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderColor: onClick ? alpha(theme.palette.primary.main, 0.5) : 'divider',
        },
      }}>
      <ReportKpiHeader icon={icon} title={title} iconColor={iconColor} iconBg={iconBg} />
      <Typography
        variant="h6"
        sx={{
          fontWeight: 800,
          color: valueColor || 'text.primary',
          lineHeight: 1.1,
          fontSize: { xs: '1.05rem', sm: '1.2rem' },
          fontVariantNumeric: 'tabular-nums',
          ml: 5,
        }}>
        {value}
      </Typography>
      {subtitle != null && subtitle !== '' ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: 5, display: 'block', fontWeight: 600, lineHeight: 1.35 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ReportKpiCard;
